import { useCurrentCast } from '@/modules/casts/compositions'
import store from '@/store'
import { ActionPaletteTabs } from '@/modules/ui/types'
import { Ref, computed } from '@vue/composition-api'
import { importDummyTweet } from '../utils/scenePresets'
import { useCurrentTeam } from '@/modules/teams/compositions'
import { SceneContent } from '@/types/sceneContent'
import { HexCode } from '@/modules/kisweui/types'

const togglePreset = async (presetId: string) => {
  const result = await store.dispatch.events.updateCurrentCast((cast) => cast.togglePreset(presetId))
  result.logIfError(`Could not toggle visibility of global preset with id [${ presetId }].`)
}

const clearPreset = async (presetId: string) => {
  const result = await store.dispatch.events.updateCurrentCast((cast) => cast.clearPreset(presetId))
  if (result.isSuccess) await store.dispatch.events.setActionPaletteTab(ActionPaletteTabs.Switcher)
  result.logIfError(`Could not clear preset with id [${ presetId }].`)
}

const setEditPresetId = async (presetId: string) => {
  await store.dispatch.events.setEditPresetId(presetId)
  await store.dispatch.events.setActionPaletteTab(ActionPaletteTabs.Preset)
}

const goToNextContentItem = async (presetId: string) => {
  const result = await store.dispatch.events.updateCurrentCast((cast) => {
    return cast.updatePreset(presetId, (preset) => preset.toggleNextContent())
  })
  result.logIfError(`Could not toggle next content item for preset [${ presetId }].`)
}

const goToPrevContentItem = async (presetId: string) => {
  const result = await store.dispatch.events.updateCurrentCast((cast) => {
    return cast.updatePreset(presetId, (preset) => preset.togglePrevContent())
  })
  result.logIfError(`Could not toggle prev content item for preset [${ presetId }].`)
}

const useScenePreset = (presetId: Ref<string>) => {

  const { currentCast, currentCastId } = useCurrentCast()
  const { teamId } = useCurrentTeam()

  const scenePreset = computed(() => currentCast.value?.scenePresets[presetId.value] ?? null)

  const currentActiveContent = computed(() => {
    if (scenePreset.value === null) return null
    return Object.values(scenePreset.value?.allContentsByOrder).find((content) => content.active) ?? null
  })

  const currentActiveContentIndex = computed(() => {
    if (scenePreset.value === null) return null
    return scenePreset.value.allContentsByOrder.findIndex((value: SceneContent) => {
      return value.id === scenePreset.value?.currentActiveContent?.id
    }) ?? null
  })

  const currentPresetName = computed(() => {
    if (scenePreset.value === null) return null
    return scenePreset.value.name ?? null
  })

  const setPresetName = async (name: string) => {
    const result = await store.dispatch.events.updateCurrentCast((cast) => {
      return cast.updatePreset(presetId.value, (preset) => {
        preset.name = name
        return preset
      })
    })
    result.logIfError(`Could not set name of the global layer preset [${ presetId }].`)
  }

  const setPresetColor = async (color: HexCode|null) => {
    const result = await store.dispatch.events.updateCurrentCast((cast) => {
      return cast.updatePreset(presetId.value, (preset) => {
        preset.color = color
        return preset
      })
    })
    result.logIfError(`Could not set color of the global layer preset [${ presetId }].`)
  }

  const globalScenePresetContentsOrdered = computed(() => {
    return Object
      .values(currentCast.value?.scenePresets[presetId.value]?.allContentsByOrder ?? {})
      .sort((a, b) => a.order > b.order ? 1 : -1)
  })

  const isTogglePrevContentEnabled = computed(() => {
    return currentActiveContentIndex.value !== null && currentActiveContentIndex.value > 0
  })

  const isToggleNextContentEnabled = computed(() => {
    if (scenePreset.value === null) return false
    if (currentActiveContentIndex.value === null || scenePreset.value.allContentsByOrder.length === 1) return false
    return currentActiveContentIndex.value < scenePreset.value.allContentsByOrder.length - 1
  })

  const reloadContent = async (contentId: string) => {
    const content = scenePreset.value?.allContentsByOrder.find((content) => content.id === contentId) ?? null
    if (content === null) throw new Error(`Content [ ${ content } ] not found. Reload failed.`)
    const result = await store.dispatch.events.updateCurrentCast((cast) => {
      return cast.updatePreset(presetId.value, (preset) => preset.refreshContentReloadKey(contentId))
    })
    result.logIfError(`Could not reload content [${ contentId }] for preset [${ presetId }].`)
  }

  return {
    clearPreset: () => clearPreset(presetId.value),
    currentActiveContent,
    currentActiveContentIndex,
    currentPresetName,
    globalScenePresetContentsOrdered,
    goToNextContentItem: () => goToNextContentItem(presetId.value),
    goToPrevContentItem: () => goToPrevContentItem(presetId.value),
    importDummyTweet: () => importDummyTweet(currentCastId.value, presetId.value, teamId.value),
    isToggleNextContentEnabled,
    isTogglePrevContentEnabled,
    reloadContent,
    scenePreset,
    setAsEditPreset: () => setEditPresetId(presetId.value),
    setPresetColor,
    setPresetName,
    togglePreset: () => togglePreset(presetId.value)
  }
}

export default useScenePreset
